<template>
  <div class="preview_container">
    <div v-if="isShowCard" class="resume_card_wrapper">
      <div class="card_title">Home Page Card</div>
      <div class="info_wrapper">
        <div class="info">
          <div class="name">{{ resumeDetail.displayName }}</div>
          <div class="type">
            {{ resumeDetail.jobStatus }}<span style="margin: 0 4px;">|</span>{{ resumeDetail.city }}
          </div>
        </div>
        <div class="avatar-wrapper">
          <img
            v-if="resumeDetail.headUrl"
            :src="resumeDetail.headUrl"
            alt=""
            class="avatar"
            crossorigin="anonymous"
            @load="isAvatarLoaded = true"
            @error="setDefaultAvatarImage"
            :style="{ opacity: isAvatarLoaded ? 1 : 0, position: isAvatarLoaded ? 'relative' : 'absolute' }"
          />
          <img v-else :src="require('@/assets/images/avatar.svg')" alt="" class="avatar" />
          <div v-if="resumeDetail.headUrl && !isAvatarLoaded" class="avatar-placeholder"></div>
        </div>
      </div>
      <div class="tag_wrapper">
        <div
          class="tag"
          v-for="(item, index) in taggedSkills.slice(0, 4)"
          :key="index"
          :class="{ 'tag-job': item.isJob, 'tag-skill': !item.isJob }"
        >
          {{ item.text }}
        </div>
      </div>
      <div class="introduce">
        {{ resumeDetail.advantage }}
      </div>
      <div v-if="resumeDetail.videoList && resumeDetail.videoList.length" class="video_wrapper">
        <div class="title">
          <img src="@/assets/images/camera.svg" alt="" class="camera_icon" />
          Skill videos
        </div>
        <div class="video_player_wrapper">
          <video
            :poster="posterSrc(resumeDetail.videoList[0])"
            :src="playSrc(resumeDetail.videoList[0])"
            oncontextmenu="return false;"
            class="video_player"
          />
        </div>
      </div>
    </div>
    <div class="resume_detail">
      <div v-if="isShowCard" class="preview_title">Resume Details Page</div>
      <div class="info_wrapper">
        <div class="info">
          <div class="name">
            <template v-if="isContentLoaded">{{ resumeDetail.displayName }}</template>
            <div v-else class="name-placeholder"></div>
          </div>
          <div class="type" v-if="isContentLoaded">
            {{ resumeDetail.jobStatus }} <span style="margin: 0 10px;">|</span> {{ resumeDetail.city }}
          </div>
          <div class="type-placeholder" v-else></div>
          <div class="contact">
            <div class="email" v-if="resumeDetail.email">
              <img src="@/assets/images/email.svg" alt="" class="email_icon" />
              <span class="contact_btn" @click="handleShowContactInfo('email')">Get email</span>
            </div>
            <div class="phone" v-if="resumeDetail.mobile">
              <img src="@/assets/images/phone.svg" alt="" class="phone_icon" />
              <span class="contact_btn" @click="handleShowContactInfo('mobile')">
                Get phone number
              </span>
            </div>
            <div class="ins" v-if="resumeDetail.link">
              <img src="@/assets/images/ins.svg" alt="" class="phone_icon" />
              <a class="contact_btn" target="_blank" :href="resumeDetail.link">
                ins
              </a>
            </div>
          </div>
        </div>
        <div class="avatar-wrapper">
          <img
            v-if="resumeDetail.headUrl"
            :src="resumeDetail.headUrl"
            alt=""
            class="avatar"
            crossorigin="anonymous"
            @load="isAvatarLoaded = true"
            @error="setDefaultAvatarImage"
            :style="{ opacity: isAvatarLoaded ? 1 : 0, position: isAvatarLoaded ? 'relative' : 'absolute' }"
          />
          <img v-else :src="require('@/assets/images/avatar.svg')" alt="" class="avatar" />
          <div v-if="resumeDetail.headUrl && !isAvatarLoaded" class="avatar-placeholder"></div>
        </div>
      </div>
      <div
        ref="tagWrapperRef"
        class="tag_wrapper"
        :class="{
          'tag_wrapper--collapsed': isOverflowing && !isTagsExpanded,
          'tag_wrapper--measuring': isMeasuring,
        }"
        @click="handleTagWrapperClick"
      >
        <div
          class="tag"
          v-for="(item, index) in taggedSkills"
          :key="index"
          :class="{ 'tag-job': item.isJob, 'tag-skill': !item.isJob }"
        >
          {{ item.text }}
        </div>
      </div>
      <div v-if="resumeDetail.videoList.length > 0" class="video_wrapper">
        <div class="title">
          <span>Skill videos</span>
        </div>
        <div class="video_content">
          <div class="video" v-for="(item, index) in resumeDetail.videoList" :key="index">
            <div class="video_player_wrapper">
              <video
                :poster="posterSrc(item)"
                oncontextmenu="return false;"
                :src="playSrc(item)"
                :id="'resumePreviewVideoPlayer' + index"
                class="video_player"
                :class="{ video_player_show: isShowCard }"
                muted
                playsinline
                webkit-playsinline
                preload="metadata"
                @click.stop="handleInitVideoView(resumeDetail.videoList, index)"
                @loadedmetadata="getVideoDuration(index)"
                @mouseenter="handleMouseEnter($event)"
                @mouseleave="handleMouseLeave($event)"
              ></video>
              <span v-show="videoDurationList[index]" class="video_duration">
                {{ formatToMinutes(videoDurationList[index]) }}
              </span>
            </div>
            <p class="video_title">{{ item.jobCategoryName }}</p>
          </div>
        </div>
      </div>
      <div v-if="resumeDetail.advantage" class="advantage_wrapper">
        <div class="title">
          <span>Personal strengths</span>
        </div>
        <div class="description">
          {{ resumeDetail.advantage }}
        </div>
      </div>
      <div v-if="resumeDetail.workExperienceList.length > 0" class="work_wrapper">
        <div class="title">
          <span>Work experience</span>
        </div>
        <div class="description">
          <div class="experience_wrapper" v-for="(item, index) in resumeDetail.workExperienceList" :key="index">
            <div class="experience">
              <div class="left_info">
                <div class="company">{{ item.companyName }}</div>
                <div class="position">{{ item.position }}</div>
              </div>
              <div class="time">
                {{ formatDate(item.startDate) }} - {{ item.endDate ? formatDate(item.endDate) : 'Present' }}
              </div>
            </div>
            <div class="content">
              {{ item.description }}
            </div>
          </div>
        </div>
      </div>
      <div v-if="resumeDetail.educationExperienceList.length > 0" class="education_wrapper">
        <div class="title">
          <span>Education Experience</span>
        </div>
        <div class="description">
          <div class="experience_wrapper" v-for="(item, index) in resumeDetail.educationExperienceList" :key="index">
            <div class="experience">
              <img :src="item.logoPngPath" alt="" class="school_icon" @error="hideInvalidImage" />
              <div class="info">
                <div class="school_row">
                  <span class="school">{{ item.school }}</span>
                  <span class="time"
                    >{{ item.startDate.slice(0, 4) }} - {{ item.endDate ? item.endDate.slice(0, 4) : 'Present' }}</span
                  >
                </div>
                <div>
                  <span class="degree">{{ item.degree }}</span>
                  <span class="major">{{ item.major }}</span>
                </div>
              </div>
            </div>
            <div class="content">
              {{ item.description }}
            </div>
          </div>
        </div>
      </div>
      <div v-if="resumeDetail.certificate" class="cert_wrapper">
        <div v-if="resumeDetail.certificate.description || resumeDetail.certificate.imageUrls" class="title">
          <span>Certificate</span>
        </div>
        <div v-if="resumeDetail.certificate.description" class="description">
          {{ resumeDetail.certificate.description }}
        </div>
        <div v-if="resumeDetail.certificate.imageUrls" class="cert_content">
          <div
            class="cert"
            v-for="(item, index) in resumeDetail.certificate.imageUrls.split(',').map((item) => item.trim())"
            :key="index"
            @click="handleInitImageView(item)"
          >
            <img :src="item" class="cert_preview" />
          </div>
        </div>
      </div>
      <div v-if="resumeDetail.evaluationList.length > 0" class="evaluation_wrapper">
        <div class="title">
          <span>References</span>
        </div>
        <div
          class="evaluation"
          v-for="(item, index) in resumeDetail.evaluationList.filter((item) => item.isHidden === 0)"
          :key="index"
        >
          <div class="label">
            <span class="name">{{ item.evaluatorName }}</span>
            <span class="relation">{{ item.evaluatorRelation }}</span>
          </div>
          <div class="content">
            {{ item.textContent }}
            <div class="file_wrapper">
              <img v-if="item.imageUrls" :src="item.imageUrls" class="img_preview" />
              <div class="video_player_wrapper">
                <video
                  v-if="item.videoUrl"
                  :poster="posterSrc(item)"
                  oncontextmenu="return false;"
                  :src="playSrc(item)"
                  class="video_player"
                  :class="{ video_player_show: isShowCard }"
                  muted
                  @click.stop="handleInitVideoView([{ videoUrl: item.videoUrl, playUrl: item.playUrl, coverUrl: item.coverUrl }], 0)"
                  @mouseenter="handleMouseEnter($event)"
                  @mouseleave="handleMouseLeave($event)"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
      <div v-if="resumeDetail.influence" class="influence_wrapper">
        <div class="title">
          <span>What am I working on?</span>
        </div>
        <div class="description">
          {{ resumeDetail.influence }}
        </div>
      </div>
      <div v-if="resumeDetail.currentTask" class="influence_wrapper">
        <div class="title">
          <span>How can the workplace support me to perform better?</span>
        </div>
        <div class="description">
          {{ resumeDetail.currentTask }}
        </div>
      </div>
    </div>
  </div>
  <a-modal class="contact_modal" v-model:open="contactVisible" @cancel="contactVisible = false" :footer="null">
    <div class="contact_content">
      {{ contactContent }}
      <a v-if="contactType == 'tips'" class="link_btn" @click="handleInitBusinessVerifyModal">
        <ArrowRightOutlined />
        Switch to Recruiter Mode
      </a>
      <div v-else class="contact_action">
        <img src="@/assets/images/copy.svg" class="action_img" @click="handleCopyContact(contactContent)" />
        <img v-if="contactContent.indexOf('@') > -1" src="@/assets/images/send_email.svg" class="action_img" />
      </div>
    </div>
  </a-modal>
  <div v-if="isShowAction" class="action_wrapper">
    <div
      v-if="store.state.currentAccount.type == 'recruiter' || !store.getters.isAuthenticated"
      class="item"
      @click="handleResumeFavoriteTaggle"
    >
      <img v-if="isCollected" src="@/assets/images/collect.svg" alt="" class="icon" />
      <img v-else src="@/assets/images/uncollect.svg" alt="" class="icon" />
      <p class="label">Save</p>
    </div>
    <div class="item" :class="{ item_loading: likeLoading }" @click="handleResumeLikeTaggle">
      <LoadingOutlined v-if="likeLoading" class="icon like_loading_icon" />
      <img v-else-if="isLike" src="@/assets/images/like.svg" alt="" class="icon" />
      <img v-else src="@/assets/images/unlike.svg" alt="" class="icon" />
      <p class="label">Like</p>
    </div>
    <div class="item" @click="shareModalvisible = true">
      <img src="@/assets/images/share.svg" alt="" class="icon" />
      <p class="label">Share</p>
    </div>
    <div class="item" @click="handleInitReportModal">
      <img src="@/assets/images/report.svg" alt="" class="icon" />
      <p class="label">Report</p>
    </div>
  </div>
  <!-- report modal -->
  <a-modal
    class="resume_detail_modal"
    v-model:open="reportModalvisible"
    title="Report"
    @cancel="reportModalvisible = false"
    :footer="null"
  >
    <a-form class="resume_form" ref="formReportRef" :model="formReportState" layout="vertical">
      <a-form-item
        validateTrigger="blur"
        label=""
        name="reason"
        :rules="[{ required: true, message: 'Enter the reason for reporting' }]"
      >
        <a-textarea
          style="margin-top:10px;"
          name="search"
          placeholder="Please enter"
          :auto-size="{ minRows: 3 }"
          :maxlength="200"
          :showCount="true"
          class="resume_textarea"
          v-model:value="formReportState.reason"
        />
      </a-form-item>
      <a-form-item label="" name="imageUrls">
        <Upload @on-upload="handleOnUploaded" />
      </a-form-item>
      <a-button
        type="primary"
        html-type="button"
        class="submit_btn"
        @click.prevent="handleSubmitReportForm"
        :disabled="isSubmitDisabled"
      >
        Done
      </a-button>
    </a-form>
  </a-modal>
  <!-- share modal -->
  <a-modal
    class="resume_detail_modal"
    v-model:open="shareModalvisible"
    @cancel="shareModalvisible = false"
    :footer="null"
    width="450px"
  >
    <div class="share_btn_wrapper">
      <div class="share_btn" @click="handleResumeShareLink">
        <img src="@/assets/images/share_link.svg" alt="" class="icon" />
        Copy Link
      </div>
      <div class="share_btn" @click="handleResumeShareQrcode">
        <img src="@/assets/images/share_qrcode.svg" alt="" class="icon" />
        Generate QR code
      </div>
    </div>
  </a-modal>
  <!-- qecode modal -->
  <a-modal
    class="resume_detail_modal"
    v-model:open="qrcodeModalvisible"
    @cancel="qrcodeModalvisible = false"
    :footer="null"
    width="450px"
  >
    <div class="qrcode_wrapper">
      <div class="info_wrapper">
        <div class="avatar-wrapper">
          <img
            v-if="resumeDetail.headUrl"
            :src="resumeDetail.headUrl"
            alt=""
            class="avatar"
            crossorigin="anonymous"
            @load="isAvatarLoaded = true"
            @error="setDefaultAvatarImage"
            :style="{ opacity: isAvatarLoaded ? 1 : 0, position: isAvatarLoaded ? 'relative' : 'absolute' }"
          />
          <img v-else :src="require('@/assets/images/avatar.svg')" alt="" class="avatar" />
          <div v-if="resumeDetail.headUrl && !isAvatarLoaded" class="avatar-placeholder"></div>
        </div>
        <div class="info">
          <div class="name">{{ resumeDetail.displayName }}</div>
          <div class="type">
            {{ resumeDetail.jobStatus }}<span style="margin: 0 4px;">|</span>{{ resumeDetail.city }}
          </div>
        </div>
      </div>
      <div class="qrcode_container">
        <canvas ref="qrCanvas"></canvas>
      </div>
      <div class="btn_wrapper">
        <a-button class="cancel_btn" @click="qrcodeModalvisible = false">Cancel</a-button>
        <a-button
          class="confirm_btn"
          type="primary"
          :loading="isDownloading"
          :disabled="isDownloading"
          @click.prevent="handleDownloadShareImage"
          >Download</a-button
        >
      </div>
    </div>
  </a-modal>
  <BusinessVerifyModal ref="BusinessVerifyModalRef" />
  <VideoViewer ref="videoViewerRef" />
  <ImageViewer ref="imageViewerRef" />
  <div v-if="qrcodeModalvisible" ref="shareContainer" class="qrcode_wrapper qrcode_share_wrapper">
    <div class="info_wrapper">
      <div class="avatar-wrapper">
        <img
          v-if="resumeDetail.headUrl"
          :src="resumeDetail.headUrl"
          alt=""
          class="avatar"
          crossorigin="anonymous"
          @error="setDefaultAvatarImage"
        />
        <img v-else :src="require('@/assets/images/avatar.svg')" alt="" class="avatar" />
      </div>
      <div class="info">
        <div class="name">{{ resumeDetail.displayName }}</div>
        <div class="type">{{ resumeDetail.jobStatus }}<span style="margin: 0 4px;">|</span>{{ resumeDetail.city }}</div>
      </div>
    </div>
    <div class="qrcode_container">
      <canvas ref="qrShareCanvas"></canvas>
    </div>
    <div class="tips">
      <img class="scan_icon" src="@/assets/images/scan.svg" />
      <span>Scan the QR code with your phone to view</span>
    </div>
    <img class="logo_share" src="@/assets/images/logo_share.svg" />
  </div>
</template>

<script setup>
import { ArrowRightOutlined, LoadingOutlined } from '@ant-design/icons-vue';
import { message } from 'ant-design-vue';
import html2canvas from 'html2canvas';
import QRious from 'qrious';
import { onMounted, onBeforeUnmount, reactive, watch, defineProps, ref, toRaw, defineEmits, nextTick, computed } from 'vue';
import { useStore } from 'vuex';

import BusinessVerifyModal from './BusinessVerifyModal.vue';

import { fetchResumeDetaileByToken } from '@/api/home.js';
import {
  fetchResumeLike,
  submitResumeLike,
  fetchResumeFavorite,
  submitResumeFavorite,
  submitResumeReport,
} from '@/api/home.js';
import { fetchResumeMobile, fetchResumeEmail } from '@/api/resume.js';
import ImageViewer from '@/components/ImageViewer.vue';
import Upload from '@/components/Upload.vue';
import VideoViewer from '@/components/VideoViewer.vue';
import { hydrateVodItems, playSrc, posterSrc } from '@/utils/vod.js';

const props = defineProps({
  resumeToken: {
    type: String,
  },
  isShowCard: {
    type: Boolean,
    default: false,
  },
  isShowAction: {
    type: Boolean,
    default: false,
  },
});
const store = useStore();

const isAvatarLoaded = ref(false);
const isContentLoaded = ref(false);

// 标签折叠：超过4行时折叠显示（带渐变遮罩），点击标签区域展开/收起
const isTagsExpanded = ref(false);
const isOverflowing = ref(false);
const isMeasuring = ref(false);
const tagWrapperRef = ref(null);
const TAG_COLLAPSE_MAX_HEIGHT = 172; // 4行（34px*4 + gap 10px*3）+ 余量
let lastMeasuredWidth = 0;
let measureRafId = 0;
let tagResizeObserver = null;

// 区分岗位和技能标签（以后端分类表为准：jobList 中的为岗位，skillList 中的为岗位下技能）
const taggedSkills = computed(() => {
  const jobNames = (store.state.categoryList.jobList || []).map((item) => item.job.trim().toLowerCase());
  const skillNames = (store.state.categoryList.skillList || []).map((skill) => skill.trim().toLowerCase());

  return resumeDetail.skillList.map((tag) => {
    const normalizedTag = tag.trim().toLowerCase();

    // 命中岗位表 -> 岗位；命中技能表 -> 技能；
    // 均未命中时为用户手动输入的自定义项（仅岗位支持自定义输入），按岗位展示
    let isJob = true;
    if (jobNames.includes(normalizedTag)) {
      isJob = true;
    } else if (skillNames.includes(normalizedTag)) {
      isJob = false;
    }

    return {
      text: tag,
      isJob,
    };
  });
});

const measureTags = async () => {
  const el = tagWrapperRef.value;
  if (!el || isMeasuring.value) return;
  // 临时取消高度限制测量自然高度（同一帧内还原，不会产生闪烁）
  isMeasuring.value = true;
  await nextTick();
  const naturalHeight = el.scrollHeight;
  isOverflowing.value = naturalHeight > TAG_COLLAPSE_MAX_HEIGHT + 2;
  isMeasuring.value = false;
  lastMeasuredWidth = el.clientWidth;
};

const scheduleMeasureTags = () => {
  if (measureRafId) cancelAnimationFrame(measureRafId);
  measureRafId = requestAnimationFrame(() => measureTags());
};

const handleTagWrapperClick = () => {
  // 仅当标签确实超出4行时才允许展开/收起
  if (!isOverflowing.value) return;
  isTagsExpanded.value = !isTagsExpanded.value;
};

const videoViewerRef = ref(null);
const handleInitVideoView = (videoListTmp, index) => {
  videoViewerRef.value.openViewer(videoListTmp, index);
};
const handleMouseEnter = (event) => {
  const video = event.target;
  video.muted = true;
  // 仅在视频准备好或已加载元数据时尝试播放
  if (video.readyState >= 1) {
    const playPromise = video.play();
    if (playPromise !== undefined) {
      playPromise.catch((error) => {
        // 捕获异步播放被打断产生的异常 (例如鼠标快速滑出时调用了 pause)
        console.warn('Video play interrupted or failed:', error.message);
      });
    }
  }
};
const handleMouseLeave = (event) => {
  const video = event.target;
  video.pause();
};
const videoDurationList = ref([]);
const getVideoDuration = (index) => {
  const el = document.getElementById('resumePreviewVideoPlayer' + index);
  if (!el) return;
  videoDurationList.value[index] = el.duration;
  if (el.readyState >= 1 && !el.currentTime) {
    try {
      el.currentTime = el.duration > 1 ? 1 : (el.duration > 0.2 ? 0.1 : 0.01);
    } catch (e) {
      // ignore
    }
  }
};
const formatToMinutes = (seconds) => {
  const totalSeconds = Math.floor(seconds);
  const minutes = Math.floor(totalSeconds / 60);
  const remainingSeconds = totalSeconds % 60;
  return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
};

const BusinessVerifyModalRef = ref(null);
const handleOpenBusinessVerifyModal = () => {
  BusinessVerifyModalRef.value.open();
};
const handleInitBusinessVerifyModal = () => {
  if (store.getters.isAuthenticated) {
    handleOpenBusinessVerifyModal();
  } else {
    contactVisible.value = false;
    handleInitLoginModal();
  }
};
const emit = defineEmits(['on-upload']);

const resumeDetail = reactive({
  firstName: '',
  lastName: '',
  displayName: '',
  jobStatus: '',
  city: '',
  email: '',
  mobile: '',
  headUrl: '',
  skillList: [],
  videoList: [],
  advantage: '',
  workExperienceList: [],
  educationExperienceList: [],
  evaluationList: [],
  influence: '',
  currentTask: '',
  certificate: null,
});
const getResumeDetail = () => {
  isAvatarLoaded.value = false;
  isContentLoaded.value = false;
  fetchResumeDetaileByToken({
    token: props.resumeToken,
  })
    .then((res) => {
      if (res.code === 0) {
        Object.assign(resumeDetail, res.data);
        hydrateVodItems(resumeDetail.videoList).then(() => {
          nextTick(() => {
            isContentLoaded.value = true;
          });
        });
      } else {
        message.error(res.msg);
        isContentLoaded.value = true;
      }
    })
    .catch((error) => {
      console.error('获取简历详情失败:', error);
      message.error('获取简历详情失败，请稍后重试');
      isContentLoaded.value = true;
    });
};

const setDefaultAvatarImage = (event) => {
  event.target.src = require('@/assets/images/avatar.svg');
  isAvatarLoaded.value = true;
};
const hideInvalidImage = (event) => {
  event.target.style.display = 'none';
};

const formatDate = (dateStr) => {
  if (!dateStr) return '';
  const parts = dateStr.split('-');
  if (parts.length === 2) {
    const year = parts[0];
    const month = parseInt(parts[1], 10);
    return `${year}-${month}`;
  } else if (parts.length === 3) {
    const year = parts[0];
    const month = parseInt(parts[1], 10);
    const day = parseInt(parts[2], 10);
    return `${year}-${month}-${day}`;
  }
  return dateStr;
};

const contactVisible = ref(false);
const contactContent = ref('');
const contactType = ref('');
const handleShowContactInfo = (type) => {
  if (store.state.currentAccount.companyStatus) {
    contactType.value = type;
    getResumeInfo(type);
  } else {
    contactContent.value =
      "Only recruiters who have passed enterprise authentication can access users' contact information.";
    contactType.value = 'tips';
    contactVisible.value = true;
  }
};
const handleCopyContact = (text) => {
  if (navigator.clipboard) {
    navigator.clipboard
      .writeText(text)
      .then(() => message.success('Copy successful'))
      .catch(() => {
        message.error('Copy Failed');
      });
  } else {
    const textarea = document.createElement('textarea');
    textarea.value = text;
    document.body.appendChild(textarea);
    textarea.select();
    try {
      document.execCommand('copy');
      message.success('Copy successful');
    } catch (err) {
      message.error('Copy Failed');
    } finally {
      document.body.removeChild(textarea);
    }
  }
};
const getResumeInfo = (type) => {
  if (type == 'email') {
    fetchResumeEmail({
      token: props.resumeToken,
    }).then((res) => {
      if (res.code === 0) {
        contactContent.value = res.data;
        contactVisible.value = true;
      } else {
        message.error(res.msg);
      }
    });
  } else {
    fetchResumeMobile({
      token: props.resumeToken,
    }).then((res) => {
      if (res.code === 0) {
        contactContent.value = res.data;
        contactVisible.value = true;
      } else {
        message.error(res.msg);
      }
    });
  }
};

const isLike = ref(false);
// 点赞请求进行中标志（唯一真值源）：请求期间拦截重复点击，避免连发多个请求、叠多个成功提示
const likeLoading = ref(false);
const getResumeLikeByToken = () => {
  fetchResumeLike({ token: props.resumeToken }).then((res) => {
    if (res.code === 0) {
      isLike.value = res.data;
    } else {
      message.error(res.msg);
    }
  });
};
const handleResumeLikeTaggle = () => {
  // 请求未完成时忽略后续点击（含 loading 图标展示期间）
  if (likeLoading.value) return;
  if (store.getters.isAuthenticated) {
    likeLoading.value = true;
    submitResumeLike({ token: props.resumeToken, isLike: isLike.value == true ? 0 : 1 })
      .then((res) => {
        if (res.code === 0) {
          message.success(res.msg);
          getResumeLikeByToken();
        } else {
          message.error(res.msg);
        }
      })
      .finally(() => {
        // 成功或失败都复位，保证按钮始终可再次操作
        likeLoading.value = false;
      });
  } else {
    handleInitLoginModal();
  }
};

const isCollected = ref(false);
const getResumeFavoriteByToken = () => {
  fetchResumeFavorite({ token: props.resumeToken }).then((res) => {
    if (res.code === 0) {
      isCollected.value = res.data;
    } else {
      message.error(res.msg);
    }
  });
};
const handleResumeFavoriteTaggle = () => {
  if (store.getters.isAuthenticated) {
    submitResumeFavorite({ token: props.resumeToken, isCollected: isCollected.value == true ? 0 : 1 }).then((res) => {
      if (res.code === 0) {
        message.success(res.msg);
        getResumeFavoriteByToken();
        emit('on-upload');
      } else {
        message.error(res.msg);
      }
    });
  } else {
    handleInitLoginModal();
  }
};
const handleInitLoginModal = () => {
  document.getElementsByClassName('login')[0].click();
  store.state.currentAccount.type = 'recruiter';
};

const handleResumeShareLink = () => {
  const shareLink = `${window.location.origin}/index/${props.resumeToken}`;
  shareModalvisible.value = false;
  handleCopyContact(shareLink);
};

const shareModalvisible = ref(false);
const qrcodeModalvisible = ref(false);
const qrValue = ref(`${window.location.origin}/index/${props.resumeToken}`);
const qrCanvas = ref(null);
const qrShareCanvas = ref(null);
const shareContainer = ref(null);
const handleResumeShareQrcode = () => {
  shareModalvisible.value = false;
  qrcodeModalvisible.value = true;
  nextTick(() => {
    const ctx = qrCanvas.value.getContext('2d');
    ctx.clearRect(0, 0, qrCanvas.value.width, qrCanvas.value.height);
    new QRious({
      element: qrCanvas.value,
      size: 150,
      value: qrValue.value,
    });
    const ctxShare = qrShareCanvas.value.getContext('2d');
    ctxShare.clearRect(0, 0, qrShareCanvas.value.width, qrShareCanvas.value.height);
    new QRious({
      element: qrShareCanvas.value,
      size: 150,
      value: qrValue.value,
    });
  });
};
const isDownloading = ref(false);
const handleDownloadShareImage = () => {
  if (isDownloading.value) return;
  isDownloading.value = true;
  html2canvas(shareContainer.value, { useCORS: true, allowTaint: true })
    .then((canvas) => {
      const imageUrl = canvas.toDataURL('image/png');
      const link = document.createElement('a');
      link.href = imageUrl;
      link.download = 'share_resume.png';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    })
    .catch((error) => {
      console.error('生成分享图片失败:', error);
      message.error('Download failed, please try again');
    })
    .finally(() => {
      isDownloading.value = false;
    });
};

const isSubmitDisabled = ref(false);
const formReportRef = ref(null);
const formReportState = reactive({
  reason: '',
  imageUrls: '',
});
const reportModalvisible = ref(false);
const handleInitReportModal = () => {
  if (store.getters.isAuthenticated) {
    reportModalvisible.value = true;
    formReportState.reason = '';
    formReportState.imageUrls = '';
  } else {
    handleInitLoginModal();
  }
};
const handleOnUploaded = (fileUrl) => {
  formReportState.imageUrls = fileUrl;
};

const handleSubmitReportForm = () => {
  if (formReportState.imageUrls === '') {
    message.error('Please attach/upload the image file');
    return;
  }
  formReportRef.value.validate().then(() => {
    isSubmitDisabled.value = false;
    submitResumeReport({ ...toRaw(formReportState), resumeId: resumeDetail.resumeId }).then((res) => {
      if (res.code === 0) {
        message.success(res.msg);
        isSubmitDisabled.value = true;
        reportModalvisible.value = false;
      } else {
        message.error(res.msg);
      }
    });
  });
};

const imageViewerRef = ref(null);
const handleInitImageView = (url) => {
  imageViewerRef.value.openViewer(url);
};

watch(
  taggedSkills,
  () => {
    isTagsExpanded.value = false;
    scheduleMeasureTags();
  },
  { flush: 'post' }
);

watch(props, () => {
  getResumeDetail();
  if (store.getters.isAuthenticated) {
    getResumeLikeByToken();
    getResumeFavoriteByToken();
  }
});

onMounted(() => {
  if (props.resumeToken) {
    getResumeDetail();
  }
  if (store.getters.isAuthenticated) {
    getResumeLikeByToken();
    getResumeFavoriteByToken();
  }
  scheduleMeasureTags();
  // 弹窗/窗口宽度变化会改变标签换行行数，宽度变化时重新判定
  tagResizeObserver = new ResizeObserver(() => {
    const el = tagWrapperRef.value;
    if (el && el.clientWidth !== lastMeasuredWidth) {
      scheduleMeasureTags();
    }
  });
  if (tagWrapperRef.value) {
    tagResizeObserver.observe(tagWrapperRef.value);
  }
  // Web 字体加载完成后行高可能变化，补测一次
  if (document.fonts && document.fonts.ready) {
    document.fonts.ready.then(() => scheduleMeasureTags());
  }
});

onBeforeUnmount(() => {
  if (measureRafId) cancelAnimationFrame(measureRafId);
  if (tagResizeObserver) tagResizeObserver.disconnect();
});
</script>

<style lang="less">
.resume_drawer {
  .ant-drawer-content-wrapper {
    width: 600px !important;
  }
  .ant-drawer-mask {
    opacity: 0.5;
  }
  .anticon {
    color: #000000;
  }
  .confirm_btn {
    background: #05644f;
    border-radius: 100px;
    color: #fff;
    font-size: 16px;
    font-weight: 500;
    line-height: normal;
    display: block;
    flex: 1;
    padding: 10px 20px;
    height: auto;
    border: none;
    &:hover {
      background: #05644f;
      color: #fff;
    }
  }
  .ant-drawer-header {
    border: none;
  }
  .ant-drawer-body {
    padding-top: 0;
  }
  .resume_form {
    position: relative;
    .title {
      font-size: 24px;
      font-weight: bold;
      color: #333333;
      margin-bottom: 10px;
      .description {
        font-size: 14px;
        color: #3d3d3d;
        margin-top: 10px;
      }
    }
    .avatar_wrapper {
      text-align: center;
      .avatar {
        margin: 30px auto 10px;
        img {
          width: 80px;
          height: 80px;
          cursor: pointer;
          border-radius: 50%;
          border: 1px solid rgba(5, 100, 79, 0.3);
          object-fit: cover;
        }
      }
      .update {
        color: #0256ff;
        font-size: 14px;
        font-weight: 500;
        margin-bottom: 20px;
        cursor: pointer;
      }
    }
    .ant-input {
      border: 1px solid rgba(5, 100, 79, 0.5);
      box-shadow: 0px 4px 10px 0px rgba(0, 0, 0, 0.03);
      border-radius: 16px;
      padding: 10px;
      font-size: 14px;
      color: #05644f;
    }
    .ant-input::placeholder {
      color: rgba(5, 100, 79, 0.33);
    }
    .ant-form-item-label > label {
      font-size: 14px;
      font-weight: 500;
      color: #3d3d3d;
      width: 100%;
      padding-bottom: 4px;
      white-space: nowrap;
    }
    .ant-form-item .ant-form-item-required:not(.ant-form-item-required-mark-optional)::before {
      display: none;
    }
    .flex_box {
      display: flex;
      justify-content: space-between;
      flex-wrap: wrap;
      .label {
        padding-bottom: 12px;
        font-size: 14px;
        font-weight: 500;
        color: #3d3d3d;
        width: 100%;
      }
      .ant-form-item,
      .custom_datepicker {
        flex: 1;
        margin-right: 20px;
        &:last-child {
          margin-right: 0;
        }
      }
      .custom_datepicker {
        border: 1px solid rgba(5, 100, 79, 0.5);
        box-shadow: 0px 4px 10px 0px rgba(0, 0, 0, 0.03);
        border-radius: 16px;
        padding: 10px;
        color: #05644f;
        margin-bottom: 24px;
        .ant-picker-suffix .anticon,
        .ant-picker-input > input {
          color: #05644f;
          font-size: 16px;
          &::placeholder {
            color: rgba(5, 100, 79, 0.33);
          }
        }
      }
    }
    .resume_select {
      margin-right: 15px;
      min-width: 120px;
      margin-bottom: 0 !important;
      &:hover {
        .ant-select-arrow {
          color: #ffffff;
        }
      }
      .ant-select-selector {
        border: 1px solid rgba(5, 100, 79, 0.5) !important;
        box-shadow: 0px 4px 10px 0px rgba(0, 0, 0, 0.03);
        color: #05644f;
        border-radius: 16px;
        box-shadow: none !important;
        height: 48px;
        .ant-select-selection-item {
          color: #05644f;
          line-height: 42px;
        }
      }
      .ant-select-arrow {
        color: #05644f;
      }
    }
    .tips {
      color: rgba(61, 61, 61, 0.5);
      font-size: 14px;
      font-weight: 300;
      white-space: nowrap;
      margin-left: 10px;
    }
    .add_position {
      border: 1px dashed rgba(5, 100, 79, 0.5);
      box-shadow: 0px 4px 10px 0px rgba(0, 0, 0, 0.05);
      color: rgba(5, 100, 79, 0.3);
      border-radius: 8px;
      padding: 12px;
      margin: 30px auto;
      cursor: pointer;
      .add_icon {
        width: 16px;
        height: 16px;
        margin-right: 10px;
        vertical-align: text-bottom;
      }
    }
    .job_position {
      margin: 30px auto;
      .position {
        padding: 12px;
        color: #05644f;
        border-radius: 16px;
        border: 1px solid rgba(5, 100, 79, 0.5);
        box-shadow: 0px 4px 10px 0px rgba(0, 0, 0, 0.03);
        cursor: pointer;
        .update_icon {
          float: right;
          margin-top: 3px;
        }
      }
      .label {
        font-size: 14px;
        font-weight: 500;
        color: #3d3d3d;
        margin-bottom: 12px;
        position: relative;
      }
      .action_wrapper {
        position: absolute;
        right: 0;
        top: 0;
        .action_icon {
          width: 16px;
          height: 16px;
          margin: 0 10px;
          cursor: pointer;
        }
      }
    }
    .video_action_wrapper {
      text-align: right;
      color: #05644f;
      font-size: 16px;
      font-weight: 500;
      margin-bottom: 10px;
      .add_btn {
        cursor: pointer;
        img {
          margin-right: 10px;
          vertical-align: text-bottom;
        }
      }
    }
    .ai_textarea_wrapper {
      margin: 25px 0;
      .label {
        padding-bottom: 12px;
        font-size: 14px;
        font-weight: 500;
        color: #3d3d3d;
        width: 100%;
      }
      .resume_textarea {
        textarea {
          padding: 20px 20px 80px 20px;
        }
      }
      .ai_btn {
        background: #f78335;
        color: #ffffff;
        font-size: 16px;
        font-weight: 500;
        position: absolute;
        padding: 6px 25px;
        bottom: 20px;
        left: 20px;
        border-radius: 100px;
        cursor: pointer;
        .icon {
          width: 18px;
          height: 18px;
          margin-right: 10px;
          vertical-align: text-bottom;
        }
      }
      .ant-input-textarea-show-count::after {
        color: #d1d1d1;
        white-space: nowrap;
        content: attr(data-count);
        pointer-events: none;
        float: right;
        position: absolute;
        bottom: 20px;
        right: 20px;
      }
    }
    .evaluation_tabs_wrapper {
      position: relative;
      .action_wrapper {
        position: absolute;
        right: 0;
        top: 0;
        z-index: 99;
        .action_icon {
          margin: 0 10px;
          cursor: pointer;
        }
        .action_switch {
          margin-left: 10px;
          &.ant-switch-checked {
            background-color: #05644f;
          }
        }
      }
    }
  }
}
.resume_select_dropdown {
  background: #fff;
  min-width: 200px !important;
  padding: 10px;
  .ant-select-item {
    color: #05644f;
    background: #fff;
    &:hover,
    &.ant-select-item-option-active,
    &.ant-select-item-option-selected {
      background: rgba(5, 100, 79, 0.1);
      color: #05644f;
    }
  }
  .ant-input {
    border: 1px solid rgba(5, 100, 79, 0.5);
    color: #05644f;
    border-radius: 16px;
    box-shadow: none;
    &::placeholder {
      color: rgba(5, 100, 79, 0.3);
    }
  }
}
.custom_datepicker_popup {
  .ant-picker-cell-in-view.ant-picker-cell-selected .ant-picker-cell-inner,
  .ant-picker-cell-in-view.ant-picker-cell-range-start .ant-picker-cell-inner,
  .ant-picker-cell-in-view.ant-picker-cell-range-end .ant-picker-cell-inner {
    color: #fff;
    background: #05644f;
  }
  .ant-picker-cell-in-view.ant-picker-cell-today .ant-picker-cell-inner::before {
    border: 1px solid #05644f;
  }
  .ant-picker-today-btn,
  .ant-picker-header-view button:hover {
    color: #05644f;
  }
}
.contact_modal {
  .ant-modal-content {
    border-radius: 20px;
  }
  .ant-modal-close-icon {
    color: #05644f;
  }
  .contact_content {
    margin: 50px auto;
    text-align: center;
    font-size: 16px;
    font-weight: normal;
    .contact_action {
      .action_img {
        width: 18px;
        height: 18px;
        margin: 30px;
        cursor: pointer;
      }
    }
  }
  .link_btn {
    margin-top: 15px;
    display: block;
    color: #0256ff;
    .anticon {
      margin-right: 10px;
    }
  }
}
.evaluation_modal {
  .ant-modal-title,
  .ant-modal-header {
    margin-bottom: 0;
  }
  .tips {
    color: #05644f;
    font-size: 16px;
    font-weight: 300;
  }
  .section_wrapper {
    margin: 30px 0;
    .section {
      border: 1px solid rgba(5, 100, 79, 0.3);
      border-radius: 16px;
      padding: 10px 20px;
      position: relative;
      margin: 25px auto;
      font-size: 16px;
      font-weight: bold;
      cursor: pointer;
      color: #05644f;
      .label_icon {
        width: 20px;
        height: 20px;
        margin-right: 10px;
      }
      .arrow_icon {
        width: 20px;
        height: 20px;
        position: absolute;
        right: 10px;
      }
    }
  }
}
.suggestions_list {
  position: absolute;
  background: #ffffff;
  box-shadow: 0px 4px 10px 0px rgba(0, 0, 0, 0.3);
  border-radius: 8px;
  width: 100%;
  margin-top: 10px;
  z-index: 99;
  li {
    list-style: none;
    padding: 10px 20px;
    color: #05644f;
    font-size: 16px;
    cursor: pointer;
    &:hover {
      background: rgba(5, 100, 79, 0.1);
    }
    .position {
      margin-left: 20px;
      color: rgba(5, 100, 79, 0.5);
    }
  }
}
.evaluation {
  margin-bottom: 15px;
  .label {
    font-weight: 500;
    font-size: 14px;
    margin-bottom: 15px;
    .name {
      font-weight: bold;
      color: #3d3d3d;
      margin-right: 20px;
    }
    .relation {
      font-weight: 300;
      color: rgba(61, 61, 61, 0.5);
    }
  }
  .content {
    position: relative;
    border: 1px solid rgba(149, 184, 159, 0.5);
    box-shadow: 0px 4px 10px 0px rgba(0, 0, 0, 0.05);
    padding: 32px;
    border-radius: 8px;
    &::before {
      content: '';
      position: absolute;
      top: -10px;
      left: 20px;
      width: 0;
      height: 0;
      border-left: 10px solid transparent;
      border-right: 10px solid transparent;
      border-bottom: 10px solid rgba(149, 184, 159, 0.5);
    }
    &::after {
      content: '';
      position: absolute;
      top: -8px;
      left: 21px;
      width: 0;
      height: 0;
      border-left: 9px solid transparent;
      border-right: 9px solid transparent;
      border-bottom: 9px solid #ffffff;
    }
    line-height: 1.8;
    font-size: 15px;
    font-weight: 400;
    white-space: pre-wrap;
    color: #4a4a4a;
    letter-spacing: 0.1px;
    word-break: break-word;
    .file_wrapper {
      margin-top: 15px;
      .img_preview {
        max-width: 230px;
        height: auto;
        border-radius: 8px;
      }
      .video_player {
        width: 230px;
        height: auto;
        max-height: 130px;
        border-radius: 8px;
        background: #333333;
        cursor: pointer;
      }
    }
  }
}
.resume_detail_modal {
  position: relative;
  .ant-modal-content {
    border-radius: 20px;
  }
  .action_wrapper {
    position: absolute;
    right: -120px;
    top: 0;
    background: #b8dfc8;
    box-sizing: border-box;
    border: 1px solid rgba(5, 100, 79, 0.5);
    box-shadow: 0px 4px 10px 0px rgba(0, 0, 0, 0.05);
    border-radius: 100px;
    padding: 15px;
    color: #05644f;
    .item {
      text-align: center;
      cursor: pointer;
      border-top: 1px solid rgba(5, 100, 79, 0.25);
      padding: 10px 0;
      &:first-child {
        border-top: none;
      }
      p {
        margin: 10px 0;
      }
      img {
        width: 24px;
        height: 24px;
      }
      // 点赞请求中：禁用观感 + loading 图标与原心形图标同尺寸（LoadingOutlined 自带旋转动画）
      &.item_loading {
        cursor: wait;
        opacity: 0.65;
        .like_loading_icon {
          font-size: 24px;
          line-height: 24px;
        }
      }
    }
  }
  .ant-modal-title {
    color: #05644f;
    font-size: 20px;
    font-weight: bold;
    line-height: normal;
  }
  .ant-modal-close-icon {
    color: #05644f;
  }
  .ant-input {
    border: 1px solid rgba(5, 100, 79, 0.5);
    box-shadow: 0px 4px 10px 0px rgba(0, 0, 0, 0.03);
    border-radius: 16px;
    padding: 10px;
    font-size: 14px;
    color: #05644f;
  }
  .ant-input::placeholder {
    color: rgba(5, 100, 79, 0.33);
  }
  .submit_btn {
    background: #05644f;
    border-radius: 100px;
    color: #fff;
    font-size: 18px;
    font-weight: bold;
    line-height: normal;
    display: block;
    width: 100%;
    padding: 10px 20px;
    height: auto;
    margin-top: 20px;
    &:hover {
      background: #05644f;
      color: #fff;
    }
    .btn_arrow {
      margin-left: 10px;
      font-size: 18px;
    }
  }
  .resume_form {
    textarea {
      padding: 20px 20px 80px 20px;
    }
  }
  .share_btn_wrapper {
    margin-top: 40px;
    .share_btn {
      border: 1px solid rgba(5, 100, 79, 0.5);
      box-shadow: 0px 4px 10px 0px rgba(0, 0, 0, 0.03);
      border-radius: 16px;
      text-align: center;
      cursor: pointer;
      border-radius: 100px;
      background: #05644f;
      font-size: 16px;
      font-weight: bold;
      color: #ffffff;
      width: 100%;
      margin-bottom: 20px;
      padding: 12px;
      position: relative;
      cursor: pointer;
      .icon {
        width: 20px;
        height: 20px;
        position: absolute;
        left: 32px;
        top: 14px;
      }
    }
  }
}
.ant-modal-wrap::-webkit-scrollbar {
  display: none;
}
.qrcode_wrapper {
  text-align: center;
  .info_wrapper {
    margin: 20px 0;
    .avatar-wrapper {
      width: 48px;
      height: 48px;
      border-radius: 50%;
      margin: 12px auto;
      overflow: hidden;
    }
    .avatar {
      width: 100%;
      height: 100%;
      border-radius: 50%;
      object-fit: cover;
    }
    .avatar-placeholder {
      width: 100%;
      height: 100%;
      border-radius: 50%;
      background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
      background-size: 200% 100%;
      animation: shimmer 1.5s infinite;
    }
    .info {
      .name {
        font-size: 14px;
        font-weight: bold;
        color: #3d3d3d;
      }
      .type {
        font-size: 14px;
        color: #888;
        margin-top: 5px;
      }
    }
  }
  .qrcode_container {
    text-align: center;
    width: fit-content;
    margin: 40px auto;
    padding: 8px;
    border: 1px solid #cdedd6;
    border-radius: 16px;
  }
  .btn_wrapper {
    display: flex;
    justify-content: space-between;
    width: 100%;
    gap: 20px;
  }
  .confirm_btn,
  .cancel_btn {
    background: #05644f;
    border-radius: 100px;
    color: #fff;
    font-size: 16px;
    font-weight: bold;
    line-height: normal;
    display: block;
    flex: 1;
    padding: 16px 20px;
    height: auto;
    border: none;
    &.cancel_btn {
      background: rgba(5, 100, 79, 0.3);
      color: #fff;
    }
    &.confirm_btn {
      background: #05644f;
      color: #fff;
    }
  }
  .tips {
    display: flex;
    gap: 12px;
    justify-content: center;
    align-items: center;
  }
  .scan_icon {
    width: 20px;
    height: 20px;
    flex-shrink: 0;
  }
  .logo_share {
    width: 180px;
    height: auto;
  }
}
.qrcode_share_wrapper {
  position: absolute;
  z-index: -1;
  padding: 40px 40px 80px;
  top: 0;
  transform: scale(2);
  .info_wrapper {
    .avatar-wrapper {
      width: 96px;
      height: 96px;
    }
  }
  .qrcode_container {
    margin-top: 60px;
  }
  .logo_share {
    width: 90px;
    height: auto;
    margin-top: 100px;
  }
  .tips {
    color: #888;
    align-items: center;
    span {
      text-align: center;
      line-height: 1.4;
    }
  }
}

@keyframes shimmer {
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: -200% 0;
  }
}

/* 真机窄屏（initial-scale=1）：各类固定宽弹窗改流式宽度，评价气泡收紧留白 */
@media (max-width: 640px) {
  .ant-modal.resume_detail_modal,
  .ant-modal.contact_modal,
  .ant-modal.evaluation_modal {
    width: calc(100vw - 24px) !important;
  }
  // 胶囊放白色 ant-modal-content 顶边外侧上方，与卡片顶留 16px 间隙
  // 胶囊参照系 = .ant-modal-content（因为它有 position:relative）
  // modal top = 114px（.ant-modal 默认 100px，此处 !important 覆盖）
  // 胶囊高 60px（padding 6 + item 48 + padding 6）
  // top: -92px（胶囊底 = 114 - 92 + 60 = 82px，卡片顶 = 114px，间隙 32px → 够宽）
  .ant-modal.resume_detail_modal {
    top: 114px !important;
    .action_wrapper {
      position: absolute !important;
      top: -92px !important;
      right: auto !important;
      left: 50% !important;
      transform: translateX(-50%);
      z-index: 10;
      width: calc(100vw - 48px);
      max-width: 320px;
      padding: 6px;
      display: flex !important;
      flex-wrap: nowrap !important;
      justify-content: space-around !important;
      align-items: center !important;
      .item {
        width: 25%;
        height: 48px;
        padding: 0;
        border-top: none !important;
        border-right: 1px solid rgba(5, 100, 79, 0.25);
        &:last-child {
          border-right: none;
        }
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        gap: 1px;
        p.label {
          margin: 0;
          font-size: 11px;
          line-height: 1;
        }
      }
    }
  }
  .evaluation {
    .content {
      padding: 16px;
      font-size: 14px;
    }
  }
}
</style>

<style scoped lang="less">
.preview_container {
  display: flex;
  align-content: space-between;
  gap: 30px;
  .card_title,
  .preview_title {
    color: #ffffff;
    font-size: 18px;
    font-weight: 700;
    background: rgba(0, 0, 0, 0.5);
    border-radius: 20px;
    padding: 10px 20px;
    display: inline-block;
    position: absolute;
    top: -70px;
  }
  .card_title {
    left: calc(50% - 90px);
  }
  .preview_title {
    left: calc(50% - 107px);
  }
  .resume_card_wrapper {
    background-color: white;
    border-radius: 16px;
    break-inside: avoid;
    height: fit-content;
    padding: 20px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08), 0 4px 12px rgba(0, 0, 0, 0.05);
    transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
    cursor: pointer;
    margin-bottom: 20px;
    width: 340px;
    position: relative;
    border: 1px solid transparent;
    &:hover {
      transform: translateY(-4px);
      box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
      border-color: rgba(5, 100, 79, 0.1);
    }
    .info_wrapper {
      display: flex;
      position: relative;
      .avatar-wrapper {
        width: 32px;
        height: 32px;
        border-radius: 50%;
        position: absolute;
        right: 0;
        overflow: hidden;
      }
      .avatar {
        width: 100%;
        height: 100%;
        border-radius: 50%;
        object-fit: cover;
      }
      .avatar-placeholder {
        width: 100%;
        height: 100%;
        border-radius: 50%;
        background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
        background-size: 200% 100%;
        animation: shimmer 1.5s infinite;
      }
      .info {
        display: flex;
        flex-wrap: wrap;
        align-content: space-around;
        .name {
          color: #3d3d3d;
          font-size: 14px;
          font-weight: 500;
          width: 100%;
          margin-bottom: 8px;
        }
        .type {
          color: #888;
          font-size: 12px;
          width: 80%;
        }
      }
    }
    .tag_wrapper {
      display: flex;
      flex-wrap: wrap;
      margin-top: 24px;
      margin-bottom: 12px;
      gap: 8px;
      .tag {
        border-radius: 100px;
        padding: 5px 10px;
        font-size: 12px;
      }

      .tag-job {
        background: rgba(5, 100, 79, 0.1);
        color: #05644f;
      }

      .tag-skill {
        background: rgba(247, 131, 53, 0.1);
        color: #f78335;
      }
    }
    .introduce {
      color: #4b5563;
      font-size: 13px;
      line-height: 1.6;
      display: -webkit-box;
      -webkit-line-clamp: 3;
      line-clamp: 3;
      -webkit-box-orient: vertical;
      overflow: hidden;
      text-overflow: ellipsis;
      word-break: break-word;
    }
    .video_wrapper {
      margin-top: 16px;
      padding-top: 16px;
      border-top: 1px solid #f3f4f6;
      position: relative;
      .title {
        display: flex;
        align-items: center;
        font-size: 12px;
        color: #6b7280;
        font-weight: 500;
        margin-bottom: 10px;
        .camera_icon {
          width: 14px;
          height: 14px;
          margin-right: 6px;
          opacity: 0.7;
        }
      }
      .video_img {
        width: 100%;
        height: auto;
        border-radius: 10px;
        margin-top: 10px;
      }
      .video_player {
        width: 100%;
        display: block;
        object-fit: cover;
        border-radius: 10px;
        background: #f5f5f5;
      }
      .video_duration {
        color: #ffffff;
        font-size: 11px;
        font-weight: 500;
        background: rgba(0, 0, 0, 0.5);
        border-radius: 4px;
        position: absolute;
        bottom: 24px;
        right: 8px;
        padding: 2px 6px;
      }
    }
  }
  .resume_detail {
    background-color: white;
    border-radius: 20px;
    break-inside: avoid;
    height: fit-content;
    padding: 56px;
    transition: transform 0.3s;
    position: relative;
    margin: 0;
    width: 100%;
    .title {
      font-size: 20px;
      color: #1a1a1a;
      margin: 40px 0 20px;
      font-weight: 600;
      position: relative;
      letter-spacing: -0.2px;
    }
    .score_wrapper {
      background: #e5efed;
      padding: 24px;
      border-radius: 8px;
      color: #05644f;
      margin-bottom: 32px;
      position: relative;
      .label {
        font-size: 20px;
        font-weight: 600;
        margin-bottom: 12px;
        letter-spacing: -0.2px;
      }
      .tips {
        font-size: 14px;
        font-weight: 400;
        line-height: 1.6;
      }
      .score_btn_wrapper {
        position: absolute;
        right: 24px;
        top: 40px;
        .score_btn {
          border: 1px solid rgba(5, 100, 79, 0.5);
          padding: 8px 14px;
          border-radius: 100px;
          margin-left: 10px;
          cursor: pointer;
          font-size: 14px;
          font-weight: 500;
          img {
            width: 8px;
            margin-left: 6px;
          }
        }
      }
      .score_tips {
        font-size: 14px;
        opacity: 0.6;
        line-height: 1.5;
      }
    }
    .info_wrapper {
      display: flex;
      position: relative;
      .avatar-wrapper {
        width: 112px;
        height: 112px;
        border-radius: 50%;
        margin-right: 0;
        position: absolute;
        right: 0px;
        cursor: pointer;
        overflow: hidden;
      }
      .avatar {
        width: 100%;
        height: 100%;
        border-radius: 50%;
        object-fit: cover;
      }
      .avatar-placeholder {
        width: 100%;
        height: 100%;
        border-radius: 50%;
        background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
        background-size: 200% 100%;
        animation: shimmer 1.5s infinite;
      }
      .info {
        padding-right: 140px; // 为右侧 112px 头像预留空间，避免文字被头像盖住
        .name {
          color: #1a1a1a;
          font-size: 32px;
          font-weight: 700;
          width: 100%;
          letter-spacing: -0.5px;
          min-height: 32px;

          .name-placeholder {
            width: 200px;
            height: 32px;
            border-radius: 4px;
            background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
            background-size: 200% 100%;
            animation: shimmer 1.5s infinite;
          }
        }
        .type {
          color: #666666;
          margin: 16px auto 24px;
          font-size: 16px;
          font-weight: 400;
          letter-spacing: 0.2px;
          span {
            opacity: 0.4;
            margin: 0 8px;
          }
        }
        .type-placeholder {
          width: 150px;
          height: 16px;
          margin: 16px auto 24px;
          border-radius: 4px;
          background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
          background-size: 200% 100%;
          animation: shimmer 1.5s infinite;
        }
        .contact {
          display: flex;
          align-items: center;
          color: #666666;
          .contact_btn {
            color: #0256ff;
            font-weight: 500;
          }
          .email,
          .phone,
          .ins {
            display: flex;
            align-items: center;
            font-size: 14px;
            margin-right: 24px;
            cursor: pointer;
            font-weight: 400;
            letter-spacing: 0.2px;
            img {
              width: 18px;
              height: 18px;
              margin-right: 8px;
            }
          }
        }
      }
    }
    .tag_wrapper {
      display: flex;
      flex-wrap: wrap;
      margin: 32px 0;
      gap: 10px;
      position: relative;

      // 测量自然高度时临时取消限制（优先级高于折叠态）
      &--measuring {
        max-height: none !important;
        overflow: visible !important;
      }

      &--collapsed {
        max-height: 172px; // 约4行高度（标签34px/行 + gap 10px，预留余量防止末行背景被裁切）
        overflow: hidden;
        cursor: pointer;

        &::after {
          content: '';
          position: absolute;
          bottom: 0;
          left: 0;
          right: 0;
          height: 40px;
          background: linear-gradient(transparent, rgba(255, 255, 255, 0.8));
          pointer-events: none;
        }
      }

      .tag {
        border-radius: 100px;
        padding: 8px 16px;
        font-size: 12px;
        font-weight: 500;
        letter-spacing: 0.2px;
      }

      .tag-job {
        background: rgba(5, 100, 79, 0.1);
        color: #05644f;
      }

      .tag-skill {
        background: rgba(247, 131, 53, 0.1);
        color: #f78335;
      }
    }
    .video_wrapper {
      margin-top: 15px;
      .title {
        .more {
          font-size: 16px;
          color: #05644f;
          float: right;
          margin-top: 10px;
        }
      }
    }
    .advantage_wrapper {
      .description {
        font-size: 16px;
        color: #4a4a4a;
        line-height: 1.8;
        white-space: pre-wrap;
        font-weight: 400;
        letter-spacing: 0.1px;
      }
    }
    .work_wrapper {
      .experience_wrapper {
        margin: 28px auto;
        .experience {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          margin-bottom: 12px;
          font-size: 16px;
          color: #1a1a1a;
          position: relative;
          .left_info {
            display: flex;
            flex-direction: column;
            gap: 6px;
          }
          .company {
            font-weight: 600;
            font-size: 17px;
            letter-spacing: -0.2px;
          }
          .position {
            font-weight: 500;
            font-size: 15px;
            color: #4a4a4a;
            letter-spacing: 0.1px;
          }
          .time {
            color: #888;
            white-space: nowrap;
            font-size: 14px;
            font-weight: 400;
            letter-spacing: 0.2px;
          }
        }
        .content {
          line-height: 1.8;
          font-size: 15px;
          color: #4a4a4a;
          white-space: pre-wrap;
          font-weight: 400;
          letter-spacing: 0.1px;
        }
      }
    }
    .education_wrapper {
      .experience_wrapper {
        margin: 24px auto;
        .experience {
          display: flex;
          align-items: flex-start;
          margin-bottom: 12px;
          font-size: 16px;
          color: #1a1a1a;
          gap: 12px;
          position: relative;
          .school_icon {
            width: 52px;
            height: 52px;
            margin-right: 12px;
          }
          .info {
            flex: 1;
            .school_row {
              display: flex;
              justify-content: space-between;
              align-items: center;
              margin-bottom: 8px;
            }
            .school {
              font-weight: 600;
              font-size: 16px;
              margin-right: 24px;
              letter-spacing: -0.2px;
            }
            .degree,
            .major {
              font-weight: 500;
              font-size: 15px;
              color: #4a4a4a;
              margin-right: 20px;
              letter-spacing: 0.1px;
            }
            .time {
              font-weight: 400;
              color: #888;
              white-space: nowrap;
              font-size: 14px;
              letter-spacing: 0.2px;
            }
          }
        }
        .content {
          line-height: 1.8;
          font-size: 15px;
          color: #4a4a4a;
          white-space: pre-wrap;
          font-weight: 400;
          letter-spacing: 0.1px;
        }
      }
    }
    .cert_wrapper {
      .description {
        font-size: 15px;
        font-weight: 400;
        color: #4a4a4a;
        margin-bottom: 20px;
        white-space: pre-wrap;
        line-height: 1.6;
        letter-spacing: 0.1px;
      }
      .cert_content {
        display: flex;
        flex-wrap: wrap;
        justify-content: flex-start;
        gap: 16px;
        height: auto;
        .cert {
          width: calc(33.33% - 11px);
          border-radius: 8px;
          position: relative;
          background: #f5f5f5;
          display: flex;
          align-items: center;
          justify-content: center;
          overflow: hidden;
          .cert_preview {
            width: 100%;
            height: auto;
            aspect-ratio: 16 / 9;
            object-fit: cover;
            cursor: pointer;
            border-radius: 0;
          }
        }
      }
    }
    .evaluation_wrapper {
      margin-bottom: 48px;
    }
    .influence_wrapper {
      margin-bottom: 48px;
      .description {
        font-size: 15px;
        font-weight: 400;
        color: #4a4a4a;
        white-space: pre-wrap;
        line-height: 1.8;
        letter-spacing: 0.1px;
      }
    }
    .edit_icon {
      margin-left: 12px;
      cursor: pointer;
    }
    .edit_icon_right {
      position: absolute;
      right: 0;
      top: 10px;
    }
    .add_wrapper {
      border: 1px dashed rgba(5, 100, 79, 0.5);
      box-shadow: 0px 4px 10px 0px rgba(0, 0, 0, 0.05);
      text-align: center;
      padding: 16px;
      border-radius: 8px;
      cursor: pointer;
      .add_icon {
        width: 16px;
        height: 16px;
        vertical-align: text-bottom;
      }
    }
    .resume_action_wrapper {
      position: absolute;
      right: -120px;
      top: 50%;
      background: #b8dfc8;
      box-sizing: border-box;
      border: 1px solid rgba(5, 100, 79, 0.5);
      box-shadow: 0px 4px 10px 0px rgba(0, 0, 0, 0.05);
      border-radius: 100px;
      padding: 15px;
      color: #05644f;
      .item {
        text-align: center;
        cursor: pointer;
        border-top: 1px solid rgba(5, 100, 79, 0.25);
        padding: 10px 0;
        &:first-child {
          border-top: none;
        }
        p {
          margin: 10px 0;
        }
      }
    }
  }
  .edit_resume_detail {
    margin-right: 630px !important;
  }
  .video_content {
    display: flex;
    flex-wrap: wrap;
    justify-content: flex-start;
    gap: 24px;
    .video {
      width: calc(33.33% - 16px);
      position: relative;
      cursor: pointer;
      .video_title {
        font-size: 14px;
        color: #333333;
        margin-top: 12px;
        margin-bottom: 0;
        word-break: break-word;
        overflow: hidden;
        display: -webkit-box;
        -webkit-box-orient: vertical;
        -webkit-line-clamp: 2;
        line-height: 20px;
      }
      .video_player_wrapper {
        position: relative;
        overflow: hidden; /* 核心：专门裁剪视频边缘 */
        border-radius: 8px;
        background: #000; /* 这里的黑色背景只占据视频画面区域 */
      }
      .video_player {
        height: 370px;
        width: 100%;
        background: #000;
        object-fit: cover;
        display: block;
        &.video_player_show {
          height: 394px;
        }
      }
      .video_duration {
        color: #ffffff;
        font-size: 12px;
        background: rgba(0, 0, 0, 0.5);
        border-radius: 4px;
        position: absolute;
        bottom: 10px; /* 调整为相对于 wrapper 的底部 */
        right: 10px;
        padding: 2px 5px;
      }
      .mask {
        display: none;
      }
      .video_content_action_wrapper {
        display: none;
        position: absolute;
        left: 0;
        top: 0;
        z-index: 99;
        width: 100%;
        height: 100%;
        .video_content_action {
          margin: 20px auto;
          display: flex;
          justify-content: space-between;
          align-items: center;
          width: 80%;
          .action_icon {
            width: 18px;
            height: 18px;
            margin: 0 10px;
            cursor: pointer;
            &.action_disabled {
              opacity: 0.5;
              cursor: default;
            }
          }
          &:first-child {
            .action_icon {
              width: 16px;
              height: 16px;
            }
          }
        }
      }
      &:hover {
        .mask {
          display: block;
        }
        .video_content_action_wrapper {
          display: block;
        }
      }
    }
    .add_video {
      width: calc(33.33% - 10px);
      height: 100%;
      display: flex;
      justify-content: center;
      align-items: center;
      border: 1px dashed rgba(5, 100, 79, 0.5);
      box-shadow: 0px 4px 10px 0px rgba(0, 0, 0, 0.05);
      border-radius: 8px;
      cursor: pointer;
      height: 150px;
      img {
        width: 20px;
        height: 20px;
      }
    }
  }
  .mask {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: linear-gradient(0deg, rgba(0, 0, 0, 0.75), rgba(0, 0, 0, 0.75));
  }
}

/* 真机窄屏（initial-scale=1）：简历详情卡片适配，层级与桌面规则对齐保证覆盖 */
@media (max-width: 640px) {
  .preview_container {
    .resume_detail {
      padding: 20px 18px;
      border-radius: 16px;
      .title {
        font-size: 18px;
        margin: 28px 0 14px;
        // 手机端大模块（技能视频/Personal strengths/Work/Education/Certificate/References 等）
        // 标题上方加分割线，模块间距 28px 的下半段用 padding 撑出线的呼吸空间
        border-top: 1px solid #e5e7eb;
        padding-top: 16px;
      }
      .score_wrapper {
        padding: 16px;
        margin-bottom: 24px;
        .label {
          font-size: 17px;
        }
        // 桌面端评分按钮绝对定位于右侧，窄屏改为跟随内容排布
        .score_btn_wrapper {
          position: static;
          margin-top: 12px;
          .score_btn {
            margin-left: 0;
            margin-right: 10px;
          }
        }
      }
      .info_wrapper {
        .avatar-wrapper {
          width: 64px;
          height: 64px;
        }
        .info {
          padding-right: 76px; // 为缩小后的 64px 头像留空间
          .name {
            font-size: 24px;
            min-height: 24px;
            letter-spacing: -0.3px;
            .name-placeholder {
              width: 140px;
              height: 24px;
            }
          }
          .type {
            font-size: 14px;
            margin: 10px 0 14px;
          }
          .type-placeholder {
            width: 120px;
            height: 14px;
            margin: 10px 0 14px;
          }
          .contact {
            flex-wrap: wrap;
            .email,
            .phone,
            .ins {
              font-size: 13px;
              margin-right: 16px;
              margin-bottom: 6px;
              img {
                width: 16px;
                height: 16px;
                margin-right: 6px;
              }
            }
          }
        }
      }
      .tag_wrapper {
        margin: 20px 0;
        gap: 8px;
        .tag {
          padding: 6px 12px;
        }
      }
      .video_wrapper {
        .title {
          .more {
            font-size: 14px;
          }
        }
      }
      .advantage_wrapper {
        .description {
          font-size: 15px;
          line-height: 1.7;
        }
      }
      .work_wrapper {
        .experience_wrapper {
          margin: 18px auto;
          .experience {
            font-size: 14px;
            .company {
              font-size: 16px;
            }
            .position {
              font-size: 14px;
            }
            .time {
              font-size: 13px;
            }
          }
          .content {
            font-size: 14px;
          }
        }
      }
      .education_wrapper {
        .experience_wrapper {
          margin: 18px auto;
          .experience {
            gap: 10px;
            font-size: 14px;
            .school_icon {
              width: 40px;
              height: 40px;
              margin-right: 0;
            }
            .info {
              .school_row {
                flex-wrap: wrap;
                row-gap: 4px;
              }
              .school {
                font-size: 15px;
                margin-right: 12px;
              }
              .degree,
              .major {
                font-size: 14px;
                margin-right: 12px;
              }
              .time {
                font-size: 13px;
              }
            }
          }
          .content {
            font-size: 14px;
          }
        }
      }
      .cert_wrapper {
        .description {
          font-size: 14px;
        }
        .cert_content {
          gap: 10px;
          .cert {
            width: calc(33.33% - 7px);
          }
        }
      }
      .evaluation_wrapper,
      .influence_wrapper {
        margin-bottom: 28px;
      }
      .influence_wrapper {
        .description {
          font-size: 14px;
        }
      }
    }
    // 技能视频桌面三列（370px 高），窄屏改两列并降低高度，避免变成窄长条
    .video_content {
      gap: 12px;
      .video {
        width: calc(50% - 6px);
        .video_title {
          font-size: 13px;
          line-height: 18px;
          margin-top: 8px;
        }
        .video_player {
          height: 190px;
          &.video_player_show {
            height: 206px;
          }
        }
        .video_duration {
          font-size: 11px;
        }
      }
    }
  }
}
</style>
